import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, PencilSimple, Trash, PaperPlaneTilt, ArrowClockwise, Heart, Eye, ChatCircle, ArrowSquareOut, Warning } from "@phosphor-icons/react";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { api } from "../../lib/api";
import { NETWORKS, NetworkDot, StatusBadge, fmtDate, errMsg } from "./socialShared";

const FILTERS = [
  { id: "", label: "Tous" }, { id: "pending_approval", label: "À valider" }, { id: "scheduled", label: "Programmés" },
  { id: "published,partial", label: "Publiés" }, { id: "failed", label: "Échecs" }, { id: "draft,rejected", label: "Brouillons / refusés" },
];

function NetworkResults({ post }) {
  return (
    <div className="flex flex-wrap gap-2">
      {post.networks.map((n) => {
        const r = post.results?.[n]; const s = post.stats?.[n];
        return (
          <span key={n} className="inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[11px]" title={r?.error || ""} data-testid={`post-${post.id}-net-${n}`}>
            <NetworkDot id={n} size={12} muted={r?.status === "error"} />
            {r?.status === "error" && <Warning size={11} className="text-red-600" weight="fill" />}
            {r?.status === "ok" && r.processing && <span className="text-muted-foreground">en traitement</span>}
            {s && <><Heart size={11} />{s.likes ?? 0}<ChatCircle size={11} />{s.comments ?? 0}{s.views != null && <><Eye size={11} />{s.views}</>}</>}
            {r?.url && <a href={r.url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}><ArrowSquareOut size={11} /></a>}
          </span>
        );
      })}
    </div>
  );
}

function Errors({ post }) {
  const errs = Object.entries(post.results || {}).filter(([, r]) => r.status === "error");
  if (!errs.length) return null;
  return <div className="mt-2 space-y-0.5">{errs.map(([n, r]) => <div key={n} className="text-[11px] text-red-700" data-testid={`post-${post.id}-error-${n}`}>{NETWORKS[n]?.label} : {r.error}</div>)}</div>;
}

function PostCard({ post, isAdmin, userId, onEdit, onReject, reload }) {
  const act = async (fn, ok) => { try { await fn(); toast.success(ok); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const mine = post.author_id === userId;
  const editable = ["draft", "pending_approval", "rejected", "scheduled", "failed"].includes(post.status) && (isAdmin || mine);
  const thumb = post.media?.[0];
  return (
    <div className="flex flex-col gap-3 rounded-xl border bg-card p-3 sm:flex-row" data-testid={`post-card-${post.id}`}>
      <div className="flex min-w-0 flex-1 gap-3">
      <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-muted">
        {thumb && (thumb.kind === "video" ? <video src={thumb.url} className="h-full w-full object-cover" muted /> : <img src={thumb.url} alt="" className="h-full w-full object-cover" />)}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <StatusBadge status={post.status} />
          <span data-testid={`post-${post.id}-date`}>{fmtDate(post.published_at || post.scheduled_at)}</span>
          <span>· {post.author_name}</span>
          {post.media?.length > 1 && <span>· {post.media.length} médias</span>}
        </div>
        <p className="mt-1 line-clamp-2 text-sm" data-testid={`post-${post.id}-text`}>{post.text || <em className="text-muted-foreground">(sans texte)</em>}</p>
        <div className="mt-2"><NetworkResults post={post} /></div>
        {post.rejection_reason && <div className="mt-1 text-[11px] text-rose-700">Motif du refus : {post.rejection_reason}</div>}
        <Errors post={post} />
      </div>
      </div>
      <div className="flex shrink-0 flex-row flex-wrap gap-1 sm:flex-col">
        {isAdmin && post.status === "pending_approval" && <>
          <Button size="sm" onClick={() => act(() => api.post(`/social/posts/${post.id}/approve`), "Post validé")} data-testid={`post-${post.id}-approve`}><Check size={14} className="mr-1" />Valider</Button>
          <Button size="sm" variant="outline" onClick={() => onReject(post)} data-testid={`post-${post.id}-reject`}><X size={14} className="mr-1" />Refuser</Button>
        </>}
        {isAdmin && ["scheduled", "failed", "partial"].includes(post.status) && (
          <Button size="sm" variant="outline" onClick={() => act(() => api.post(`/social/posts/${post.id}/publish-now`), "Publication lancée")} data-testid={`post-${post.id}-publish-now`}>
            <PaperPlaneTilt size={14} className="mr-1" />{post.status === "scheduled" ? "Publier" : "Réessayer"}
          </Button>
        )}
        {["published", "partial"].includes(post.status) && (
          <Button size="sm" variant="ghost" onClick={() => act(() => api.post(`/social/posts/${post.id}/refresh-stats`), "Statistiques à jour")} data-testid={`post-${post.id}-refresh`}><ArrowClockwise size={14} className="mr-1" />Stats</Button>
        )}
        {editable && <Button size="sm" variant="ghost" onClick={() => onEdit(post)} data-testid={`post-${post.id}-edit`}><PencilSimple size={14} className="mr-1" />Modifier</Button>}
        {(isAdmin || (mine && ["draft", "pending_approval", "rejected", "failed"].includes(post.status))) && post.status !== "publishing" && (
          <Button size="sm" variant="ghost" className="text-red-600" onClick={() => window.confirm("Supprimer ce post ? (il ne sera pas retiré des réseaux s'il est déjà publié)") && act(() => api.delete(`/social/posts/${post.id}`), "Post supprimé")} data-testid={`post-${post.id}-delete`}>
            <Trash size={14} className="mr-1" />Supprimer
          </Button>
        )}
      </div>
    </div>
  );
}

function RejectDialog({ post, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const submit = async () => {
    try { await api.post(`/social/posts/${post.id}/reject`, { reason }); toast.success("Post refusé"); onDone(); onClose(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!post} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="reject-dialog">
        <DialogHeader><DialogTitle>Refuser le post</DialogTitle></DialogHeader>
        <Textarea rows={3} placeholder="Motif (visible par l'auteur)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="reject-reason" />
        <DialogFooter><Button variant="ghost" onClick={onClose}>Annuler</Button><Button variant="destructive" onClick={submit} data-testid="reject-confirm">Refuser</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function PostList({ refreshKey, isAdmin, userId, onEdit, onChanged, initialFilter = "" }) {
  const [filter, setFilter] = useState(initialFilter);
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], pages: 1, total: 0 });
  const [rejecting, setRejecting] = useState(null);
  const load = () => api.get("/social/posts", { params: { status: filter || undefined, page, per_page: 25 } })
    .then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [filter, page, refreshKey]); // eslint-disable-line
  useEffect(() => { setPage(1); }, [filter]);
  const reload = () => { load(); onChanged?.(); };

  return (
    <div data-testid="social-post-list">
      <div className="mb-3 flex flex-wrap gap-1">
        {FILTERS.map((f) => (
          <Button key={f.id || "all"} size="sm" variant={filter === f.id ? "default" : "ghost"} onClick={() => setFilter(f.id)} data-testid={`posts-filter-${f.id || "all"}`}>
            {f.label}{f.id === "pending_approval" && data.pending_count > 0 ? ` (${data.pending_count})` : ""}
          </Button>
        ))}
      </div>
      <div className="space-y-2">
        {data.items.map((p) => <PostCard key={p.id} post={p} isAdmin={isAdmin} userId={userId} onEdit={onEdit} onReject={setRejecting} reload={reload} />)}
        {!data.items.length && <div className="rounded-xl border border-dashed p-10 text-center text-sm text-muted-foreground" data-testid="posts-empty">Aucun post ici pour l'instant.</div>}
      </div>
      {data.pages > 1 && (
        <div className="mt-3 flex items-center justify-end gap-2 text-sm">
          <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="posts-prev-page">Précédent</Button>
          <span className="font-mono" data-testid="posts-page-indicator">Page {page} / {data.pages}</span>
          <Button variant="secondary" size="sm" disabled={page >= data.pages} onClick={() => setPage(page + 1)} data-testid="posts-next-page">Suivant</Button>
        </div>
      )}
      {rejecting && <RejectDialog post={rejecting} onClose={() => setRejecting(null)} onDone={reload} />}
    </div>
  );
}
